import type { FieldConfig, SectionConfig } from './customViewPanel.types'
import type { CustomViewDataPoint, CustomViewTarget } from '@/contexts/products/customViewConfig'
import type { ViewMode } from '@/contexts/products/ProductsContext'
import { getLockedOff } from '@/contexts/products/customViewConfig'

export const BASE = 'products-page.custom-view-panel'

const SM_LOCKED_KEYS: Set<CustomViewDataPoint> = new Set([
  'ecodesignAndCO2',
  'homologatedIn',
  'validationStatus',
  'moq',
])

export function buildSections(viewMode: ViewMode, target: CustomViewTarget = 'gallery'): SectionConfig[] {
  return target === 'list'
    ? buildListSections()
    : buildGallerySections(viewMode)
}

function buildListSections(): SectionConfig[] {
  return [
    {
      titleKey: `${BASE}.sections.main-information`,
      fields: [
        { type: 'selectable', key: 'dmiSegmentation', labelKey: `${BASE}.fields.dmi-segmentation` },
        { type: 'mandatory', labelKey: `${BASE}.fields.supermodel-code-and-name` },
        { type: 'selectable', key: 'price', labelKey: `${BASE}.fields.price` },
        { type: 'selectable', key: 'customerReview', labelKey: `${BASE}.fields.customer-review` },
      ],
    },
    {
      titleKey: `${BASE}.sections.product-information`,
      fields: [
        { type: 'selectable', key: 'brand', labelKey: `${BASE}.fields.brand` },
        { type: 'selectable', key: 'gender', labelKey: `${BASE}.fields.gender` },
        { type: 'selectable', key: 'productNature', labelKey: `${BASE}.fields.product-nature` },
        { type: 'selectable', key: 'composition', labelKey: `${BASE}.fields.composition` },
        { type: 'selectable', key: 'productBenefits', labelKey: `${BASE}.fields.product-benefits` },
        { type: 'selectable', key: 'internetTeaser', labelKey: `${BASE}.fields.internet-teaser` },
        { type: 'selectable', key: 'designedIn', labelKey: `${BASE}.fields.designed-in` },
        { type: 'selectable', key: 'distributionCustomsZones', labelKey: `${BASE}.fields.distribution-customs-zones` },
        { type: 'selectable', key: 'availabilityDates', labelKey: `${BASE}.fields.availability-dates` },
      ],
    },
  ]
}

function buildGallerySections(viewMode: ViewMode): SectionConfig[] {
  const locked = getLockedOff(viewMode)
  const isLockedOff = (key: CustomViewDataPoint) => locked.includes(key)

  const field = (key: CustomViewDataPoint, labelKey: string): FieldConfig =>
    isLockedOff(key)
      ? {
          type: 'lockedOff',
          key,
          labelKey,
          reason: SM_LOCKED_KEYS.has(key) ? 'models' : 'supermodels',
        }
      : { type: 'selectable', key, labelKey }

  return [
    {
      titleKey: `${BASE}.sections.main-information`,
      fields: [
        {
          type: 'selectable',
          key: 'productPicture',
          labelKey: `${BASE}.fields.product-picture`,
        },
        {
          type: 'selectable',
          key: 'dmiSegmentation',
          labelKey: `${BASE}.fields.dmi-segmentation`,
        },
        // {
        //   type: 'selectable',
        //   key: 'wholesaleSegmentation',
        //   labelKey: `${BASE}.fields.wholesale-segmentation`,
        // },
        {
          type: 'mandatory',
          labelKey: `${BASE}.fields.supermodel-code-and-name`,
        },
        viewMode === 'sm'
          ? {
              type: 'lockedOff',
              labelKey: `${BASE}.fields.model-code-and-name`,
              reason: 'models',
            }
          : ({
              type: 'mandatory',
              labelKey: `${BASE}.fields.model-code-and-name`,
            } as FieldConfig),
        { type: 'selectable', key: 'price', labelKey: `${BASE}.fields.price` },
        field('customerReview', `${BASE}.fields.customer-review`),
      ],
    },
    {
      titleKey: `${BASE}.sections.product-information`,
      fields: [
        // field('modelVariations', `${BASE}.fields.model-variations`),
        field('brand', `${BASE}.fields.brand`),
        ...(viewMode === 'model'
          ? [field('sizeGrid', `${BASE}.fields.size-grid`)]
          : []),
        {
          type: 'selectable',
          key: 'gender',
          labelKey: `${BASE}.fields.gender`,
        },
        {
          type: 'selectable',
          key: 'productNature',
          labelKey: `${BASE}.fields.product-nature`,
        },
        {
          type: 'selectable',
          key: 'composition',
          labelKey: `${BASE}.fields.composition`,
        },
        field('ecodesignAndCO2', `${BASE}.fields.ecodesign-and-co2`),
        {
          type: 'selectable',
          key: 'productBenefits',
          labelKey: `${BASE}.fields.product-benefits`,
        },
        {
          type: 'selectable',
          key: 'internetTeaser',
          labelKey: `${BASE}.fields.internet-teaser`,
        },
        {
          type: 'selectable',
          key: 'designedIn',
          labelKey: `${BASE}.fields.designed-in`,
        },
        field('distributionCustomsZones', `${BASE}.fields.distribution-customs-zones`),
        // {
        //   type: 'selectable',
        //   key: 'producedIn',
        //   labelKey: `${BASE}.fields.produced-in`,
        // },
        field('homologatedIn', `${BASE}.fields.homologated-in`),
      ],
    },
    {
      titleKey: `${BASE}.sections.product-status`,
      fields: [
        {
          type: 'selectable',
          key: 'availabilityDates',
          labelKey: `${BASE}.fields.availability-dates`,
        },
        field('validationStatus', `${BASE}.fields.validation-status`),
      ],
    },
    // {
    //   titleKey: `${BASE}.sections.business-data`,
    //   fields: [
    //     { type: 'selectable', key: 'gmv', labelKey: `${BASE}.fields.gmv` },
    //     {
    //       type: 'selectable',
    //       key: 'salesQuantity',
    //       labelKey: `${BASE}.fields.sales-quantity`,
    //     },
    //     {
    //       type: 'selectable',
    //       key: 'margin',
    //       labelKey: `${BASE}.fields.margin`,
    //     },
    //     {
    //       type: 'selectable',
    //       key: 'costPlus',
    //       labelKey: `${BASE}.fields.cost-plus`,
    //     },
    //     {
    //       type: 'selectable',
    //       key: 'markup',
    //       labelKey: `${BASE}.fields.markup`,
    //     },
    //     {
    //       type: 'selectable',
    //       key: 'gmvCO2',
    //       labelKey: `${BASE}.fields.gmv-co2`,
    //     },
    //   ],
    // },
    // {
    //   titleKey: `${BASE}.sections.supply-data`,
    //   fields: [field('moq', `${BASE}.fields.moq`)],
    // },
  ]
}
